/**
 * Dead Bright effects and colour-driven UI:
 * - keeps every [data-db-colour-image] and [data-db-colour-name] in step with the chosen colour
 * - reports clicks on [data-db-cta] links as db_cta_click events
 * - <db-modes-component>   Steady / Slow / Fast demo (kept under 3 flashes a second)
 * - <db-torch-component>   the flashlight scene
 * - <db-wall-component>    the colour wall
 *
 * The hero ignition is pure CSS now (sections/db-hero.liquid, keyed on html[data-db-ignite]).
 *
 * Everything degrades to readable, server-rendered content when this file does not run.
 */
import { Component } from '@theme/component';
import { store } from '@theme/db-core';
import { emit } from '@theme/db-events';

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ---------------------------------------------------------------- colour sync */

/** @param {HTMLImageElement} img @param {{src: string, srcset: string, alt: string}} image */
async function swapImage(img, image) {
  if (img.dataset.dbCurrent === image.src) return;
  img.dataset.dbCurrent = image.src;
  const preload = new Image();
  preload.srcset = image.srcset;
  preload.sizes = img.sizes;
  preload.src = image.src;
  try {
    await preload.decode();
  } catch {
    /* fall through: swap anyway */
  }
  if (img.dataset.dbCurrent !== image.src) return; // a newer choice won
  if (reduceMotion.matches) {
    img.srcset = image.srcset;
    img.src = image.src;
    img.alt = image.alt;
    return;
  }
  img.classList.add('is-swapping');
  window.setTimeout(() => {
    img.srcset = image.srcset;
    img.src = image.src;
    img.alt = image.alt;
    requestAnimationFrame(() => img.classList.remove('is-swapping'));
  }, 180);
}

function syncColour() {
  const variant = store.accentVariant;
  if (!variant) return;
  for (const img of document.querySelectorAll('img[data-db-colour-image]')) {
    if (img instanceof HTMLImageElement) swapImage(img, variant.image);
  }
  for (const el of document.querySelectorAll('[data-db-colour-name]')) {
    if (el.textContent !== variant.label) el.textContent = variant.label;
  }
}

if (store.ready) {
  store.subscribe(syncColour);
  // the hero image carries the marker too
  for (const img of document.querySelectorAll('img[data-db-stage-img]')) {
    img.setAttribute('data-db-colour-image', '');
    if (img instanceof HTMLImageElement) img.dataset.dbCurrent = img.currentSrc || img.src;
  }
}

/* ---------------------------------------------------------------- calls to action */

document.addEventListener('click', (event) => {
  const link = event.target instanceof Element ? event.target.closest('[data-db-cta]') : null;
  if (link instanceof HTMLElement) emit('db_cta_click', { cta_id: link.dataset.dbCta ?? '', colour_key: store.accentVariant?.key ?? '' });
});

/* ---------------------------------------------------------------- mode demo */

/**
 * Steady / Slow Flash / Fast Flash (WCAG 2.3.1: 0.67 and 1.67 flashes a second, one element, never
 * black). Flashing stops by itself after 20 seconds and when the section leaves the screen. Steady is
 * always the stop control. With reduced motion the mask never animates unless the shopper opts in.
 *
 * @typedef {Object} ModesRefs
 * @property {HTMLButtonElement[]} modes
 * @property {HTMLElement} lit
 * @property {HTMLElement} status
 * @property {HTMLElement} live
 * @property {HTMLButtonElement} play
 *
 * @extends {Component<ModesRefs>}
 */
class DbModesComponent extends Component {
  requiredRefs = ['lit'];

  /** @type {number | undefined} */
  #stopTimer;
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();
    this.#apply(this.dataset.mode || 'steady', false);
    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting && this.dataset.mode !== 'steady') this.#pause();
        }
      });
      this.#observer.observe(this);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#stopTimer);
    this.#observer?.disconnect();
  }

  /** @param {Event} event */
  handleMode(event) {
    const button = /** @type {HTMLElement} */ (event.target).closest('[data-mode]');
    if (button instanceof HTMLElement) this.#apply(button.dataset.mode || 'steady', true);
  }

  /** Reduced-motion opt-in: a gentle 10-second slow pulse. */
  handlePlay() {
    this.toggleAttribute('data-gentle', true);
    window.setTimeout(() => this.removeAttribute('data-gentle'), 10500);
  }

  #pause() {
    this.#apply('steady', false);
    if (this.refs.status) this.refs.status.textContent = this.dataset.tPaused ?? '';
  }

  /** @param {string} mode @param {boolean} announce */
  #apply(mode, announce) {
    clearTimeout(this.#stopTimer);
    this.dataset.mode = mode;
    for (const button of this.refs.modes ?? []) {
      button.setAttribute('aria-pressed', String(button.dataset.mode === mode));
    }
    const label = this.querySelector(`[data-mode="${mode}"]`)?.getAttribute('data-status');
    if (this.refs.status && label) this.refs.status.textContent = label;
    if (announce && this.refs.live) {
      const key = { steady: 'tLiveSteady', slow: 'tLiveSlow', fast: 'tLiveFast' }[mode];
      this.refs.live.textContent = key ? (this.dataset[key] ?? '') : '';
    }
    if (mode !== 'steady' && !reduceMotion.matches) {
      this.#stopTimer = window.setTimeout(() => this.#pause(), 20000);
    }
  }
}

if (!customElements.get('db-modes-component')) customElements.define('db-modes-component', DbModesComponent);

/* ---------------------------------------------------------------- torch */

const TORCH_EASE = 6; // per second: the light trails the pointer a little, the same at 60 and 144 Hz
const TORCH_IDLE_MS = 4000;

/**
 * A dark scene lit only by a circle that follows the pointer (drag on touch), eased so it glides.
 * The copy is always in the DOM and readable by assistive tech. "Lights on" reveals it all, and so does
 * moving keyboard focus into the section, four seconds without any pointer movement, or reduced motion.
 *
 * @typedef {Object} TorchRefs
 * @property {HTMLElement} scene
 * @property {HTMLButtonElement} toggle
 *
 * @extends {Component<TorchRefs>}
 */
class DbTorchComponent extends Component {
  requiredRefs = ['scene'];
  #abort = new AbortController();
  /** @type {IntersectionObserver | undefined} */
  #observer;
  /** @type {number | undefined} */
  #idleTimer;
  #frame = 0;
  #last = 0;
  #moved = false;
  /** @type {{x: number, y: number} | null} latest pointer position in the viewport */
  #aim = null;
  #shown = { x: 0, y: 0 };
  #target = { x: 0, y: 0 };

  connectedCallback() {
    super.connectedCallback();
    const { scene } = this.refs;
    const { signal } = this.#abort;
    this.classList.add('is-ready');
    if (reduceMotion.matches) this.#setLit(true);

    /** @param {number} x @param {number} y */
    const aim = (x, y) => {
      this.#moved = true;
      window.clearTimeout(this.#idleTimer);
      this.#aim = { x, y };
      this.#wake();
    };
    scene.addEventListener('pointermove', (e) => aim(e.clientX, e.clientY), { signal });
    scene.addEventListener('pointerdown', (e) => aim(e.clientX, e.clientY), { signal });
    scene.addEventListener(
      'touchmove',
      (e) => {
        const t = e.touches[0];
        if (t) aim(t.clientX, t.clientY);
      },
      { signal, passive: true }
    );
    // a keyboard shopper has no pointer: reaching the section lights it (a mouse click on the button must still toggle)
    this.addEventListener(
      'focusin',
      (event) => {
        if (event.target instanceof Element && event.target.matches(':focus-visible')) this.#setLit(true);
      },
      { signal }
    );

    // the light starts near the middle so touch shoppers see the effect before they touch
    const rect = scene.getBoundingClientRect();
    this.#shown = { x: rect.width * 0.5, y: rect.height * 0.45 };
    this.#target = { ...this.#shown };
    this.#paint();

    // nobody moved a pointer over the scene for a while: show the copy rather than darkness
    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          window.clearTimeout(this.#idleTimer);
          if (entry.isIntersecting && !this.#moved) {
            this.#idleTimer = window.setTimeout(() => this.#setLit(true), TORCH_IDLE_MS);
          }
        }
      });
      this.#observer.observe(scene);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
    this.#observer?.disconnect();
    window.clearTimeout(this.#idleTimer);
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  toggleLights() {
    this.#setLit(!this.classList.contains('is-lit'));
  }

  /** @param {boolean} lit */
  #setLit(lit) {
    this.classList.toggle('is-lit', lit);
    this.refs.toggle?.setAttribute('aria-pressed', String(lit));
  }

  #wake() {
    if (this.#frame) return;
    this.#last = 0;
    this.#frame = requestAnimationFrame(this.#tick);
  }

  /** @param {number} now */
  #tick = (now) => {
    this.#frame = 0;
    const dt = this.#last ? Math.min((now - this.#last) / 1000, 0.05) : 1 / 60;
    this.#last = now;
    // one layout read, before any write
    if (this.#aim) {
      const rect = this.refs.scene.getBoundingClientRect();
      this.#target = { x: this.#aim.x - rect.left, y: this.#aim.y - rect.top };
      this.#aim = null;
    }
    const k = 1 - Math.exp(-dt * TORCH_EASE);
    this.#shown.x += (this.#target.x - this.#shown.x) * k;
    this.#shown.y += (this.#target.y - this.#shown.y) * k;
    const settled = Math.abs(this.#target.x - this.#shown.x) < 0.4 && Math.abs(this.#target.y - this.#shown.y) < 0.4;
    if (settled) this.#shown = { ...this.#target };
    this.#paint();
    if (!settled) this.#frame = requestAnimationFrame(this.#tick);
  };

  #paint() {
    const { scene } = this.refs;
    scene.style.setProperty('--tx', `${this.#shown.x.toFixed(1)}px`);
    scene.style.setProperty('--ty', `${this.#shown.y.toFixed(1)}px`);
  }
}

if (!customElements.get('db-torch-component')) customElements.define('db-torch-component', DbTorchComponent);

/* ---------------------------------------------------------------- colour wall */

/**
 * Tapping a tile lights the page in that colour and fills the active mask slot.
 * Tiles are real links to ?variant=, so they work without JavaScript.
 *
 * @extends {Component}
 */
class DbWallComponent extends Component {
  /** @param {Event} event */
  handlePick(event) {
    const tile = /** @type {HTMLElement} */ (event.target).closest('[data-variant-id]');
    if (!(tile instanceof HTMLElement) || tile.getAttribute('aria-disabled') === 'true') return;
    if (!store.ready) return;
    event.preventDefault();
    const id = Number(tile.dataset.variantId);
    store.pickColour(id);
    emit('db_colour_pick', { colour_key: store.variant(id)?.key ?? '', source: 'wall' });
    for (const other of this.querySelectorAll('[data-variant-id]')) {
      if (other === tile) other.setAttribute('aria-current', 'true');
      else other.removeAttribute('aria-current');
    }
  }
}

if (!customElements.get('db-wall-component')) customElements.define('db-wall-component', DbWallComponent);
